'use client'
import { Link } from '@/i18n/routing'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'
import { Bike, Snowflake, Wrench, type LucideIcon } from 'lucide-react'
import type { ComponentProps } from 'react'

type Href = ComponentProps<typeof Link>['href']

interface HeroCardProps {
  image: string
  alt: string
  title: string
  description: string
  rentalHref: Href
  rentalLabel: string
  workshopHref: Href
  workshopLabel: string
  RentalIcon: LucideIcon
}

// Mobile: horizontal card — image left, content right.
// Desktop (lg+): full-bleed image hero with overlaid content.
function HeroCard({
  image, alt, title, description,
  rentalHref, rentalLabel, workshopHref, workshopLabel, RentalIcon,
}: HeroCardProps) {
  const btn =
    'gap-2 bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white font-bold transition-transform hover:scale-105 hover:opacity-90 w-full lg:w-auto text-sm px-4 lg:text-lg lg:px-6'
  return (
    <div className="group relative isolate flex min-h-[10rem] overflow-hidden rounded-3xl border border-border bg-card shadow-sm lg:block lg:min-h-0 lg:aspect-[4/5] lg:border-0 lg:bg-transparent lg:shadow-none">
      {/* Image */}
      <div className="relative w-2/5 shrink-0 self-stretch lg:absolute lg:inset-0 lg:w-full">
        <Image
          src={image}
          alt={alt}
          fill
          sizes="(max-width: 1024px) 40vw, 50vw"
          className="object-cover object-top transition-transform duration-500 group-hover:scale-105"
          priority
          fetchPriority="high"
        />
      </div>

      {/* Gradient overlay — desktop only */}
      <div className="hidden lg:absolute lg:inset-0 lg:block bg-gradient-to-t from-black/60 via-black/30 to-transparent" />

      {/* Whole-card link → rental page. Sits above the image; the content
          layer is pointer-events-none so clicks fall through here, while the
          CTA buttons re-enable pointer events and keep their own targets. */}
      <Link href={rentalHref} aria-label={title} className="absolute inset-0 z-10" />

      {/* Content */}
      <div className="pointer-events-none relative z-20 flex flex-1 flex-col justify-center p-5 lg:absolute lg:inset-0 lg:items-center lg:justify-end lg:p-8 lg:pt-40 lg:pb-24 lg:text-white">
        <h2 className="mb-2 text-3xl leading-none font-bold font-[family-name:var(--font-bebas-neue)] text-card-foreground lg:mb-4 lg:text-4xl lg:text-white">
          {title}
        </h2>
        <p className="mb-4 line-clamp-3 text-sm text-muted-foreground lg:mb-6 lg:max-w-lg lg:text-center lg:text-xl lg:text-white lg:line-clamp-none">
          {description}
        </p>
        <div className="pointer-events-auto flex w-full flex-col gap-2 lg:w-auto lg:flex-row lg:gap-3">
          <Button asChild size="lg" className={btn}>
            <Link href={rentalHref}>
              <RentalIcon className="h-4 w-4 lg:h-5 lg:w-5" />
              <span>{rentalLabel}</span>
            </Link>
          </Button>
          <Button asChild size="lg" className={btn}>
            <Link href={workshopHref}>
              <Wrench className="h-4 w-4 lg:h-5 lg:w-5" />
              <span>{workshopLabel}</span>
            </Link>
          </Button>
        </div>
      </div>
    </div>
  )
}

export default function DualHeroSection() {
  const t = useTranslations('Hero')

  return (
    <div className="w-full pt-32 md:pt-40 pb-16 md:pb-24 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Main Title */}
        <div className="text-center mb-8">
          <h1 className="text-5xl md:text-7xl font-bold font-[family-name:var(--font-bebas-neue)] mb-4 max-w-4xl mx-auto">
            {t('title')}
          </h1>
          <p className="text-xl md:text-2xl text-foreground/90 max-w-3xl mx-auto mb-15">
            {t('subtitle')}
          </p>
        </div>

        {/* Dual Hero Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <HeroCard
            image="/7-laux-mtb-emtb-vtt-rental-location.png"
            alt={t('summer.title')}
            title={t('summer.title')}
            description={t('summer.description')}
            rentalHref="/location/summer"
            rentalLabel={t('summer.ctaRental')}
            workshopHref="/atelier/summer"
            workshopLabel={t('summer.ctaWorkshop')}
            RentalIcon={Bike}
          />
          <HeroCard
            image="/7-laux-ski-snowboard-rental-location-snowpark-ressort.png"
            alt={t('winter.title')}
            title={t('winter.title')}
            description={t('winter.description')}
            rentalHref="/location/winter"
            rentalLabel={t('winter.ctaRental')}
            workshopHref="/atelier/winter"
            workshopLabel={t('winter.ctaWorkshop')}
            RentalIcon={Snowflake}
          />
        </div>
      </div>
    </div>
  )
}
